import React, { useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff } from 'react-feather'; // Added for password visibility toggle
import { domain } from './Domain.jsx';
import notify from './Nots.jsx';
import 'react-toastify/dist/ReactToastify.css';

const PasswordResetForm = () => {
    const { slug, token } = useParams();
    const [isPending, setIsPending] = useState(false);
    const [login, setLogin] = useState(false);
    const regRefSuccess = useRef();
    const [errors, setErrors] = useState({});
    const navigate = useNavigate();
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [formData, setFormData] = useState({
        new_password: '',
        confirm_password: '',
    });
    const [passwordStrength, setPasswordStrength] = useState({
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false,
    });

    // Function to validate password strength
    const validatePasswordStrength = (password) => {
        return {
            length: password.length >= 8,
            uppercase: /[A-Z]/.test(password),
            lowercase: /[a-z]/.test(password),
            number: /[0-9]/.test(password),
            special: /[!@#$%^&*]/.test(password),
        };
    };

    // Handle input changes and validate password strength in real-time
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });

        if (name === 'new_password') {
            setPasswordStrength(validatePasswordStrength(value));
        }

        setErrors({ ...errors, [name]: '' }); // Clear errors when user types
    };

    // Toggle password visibility
    const togglePasswordVisibility = () => {
        setPasswordVisible(!passwordVisible);
    };

    // Validate passwords before submitting
    const validateForm = () => {
        let newErrors = {};
        const { new_password, confirm_password } = formData;
        const passwordCriteria = validatePasswordStrength(new_password);

        if (!passwordCriteria.length) newErrors.new_password = 'Password must be at least 8 characters long';
        if (!passwordCriteria.uppercase) newErrors.new_password = 'Password must contain at least one uppercase letter';
        if (!passwordCriteria.lowercase) newErrors.new_password = 'Password must contain at least one lowercase letter';
        if (!passwordCriteria.number) newErrors.new_password = 'Password must contain at least one number';
        if (!passwordCriteria.special) newErrors.new_password = 'Password must contain at least one special character (!@#$%^&*)';

        if (!confirm_password) {
            newErrors.confirm_password = 'Confirm password is required';
        } else if (new_password !== confirm_password) {
            newErrors.confirm_password = 'Passwords do not match';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrors({});

        if (!validateForm()) {
            return;
        }

        setIsPending(true);
        if (regRefSuccess.current) {
            regRefSuccess.current.style.display = 'block';
            regRefSuccess.current.textContent = 'Requesting password reset...';
        }

        try {
            const response = await fetch(`${domain}/accounts/reset-password/${slug}/${token}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
            });

            const responseData = await response.json();

            if (!response.ok) {
                throw new Error(responseData.message || `Error ${response.status}`);
            }

            // Success
            notify(responseData.message, 'success');
            if (regRefSuccess.current) {
                regRefSuccess.current.textContent = responseData.message;
            }
            setLogin(true);

            setTimeout(() => {
                if (regRefSuccess.current) {
                    regRefSuccess.current.style.display = 'none';
                }
                navigate('/login');
            }, 5000);
        } catch (error) {
            console.error('Error:', error);
            notify(error.message, 'error');
            if (regRefSuccess.current) {
                regRefSuccess.current.textContent = error.message || 'Something went wrong!';
            }
        } finally {
            setIsPending(false);
        }
    };

    return (
        <motion.form
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.5 }}
            onSubmit={handleSubmit}
            className="forgotPassword bg-white"
        >
            <h2 className="fs-5 pt-2 lead fw-bold fg text-success">Reset Your Password</h2>
            <p ref={regRefSuccess} className="text-success fw-bold m-0" style={{ display: 'none' }}></p>
            <div className="position-relative">
                <label htmlFor="new_password" className="pt-2" style={{ fontSize: '0.875rem' }}>
                    New Password:
                </label>
                <input
                    type={passwordVisible ? 'text' : 'password'}
                    id="new_password"
                    name="new_password"
                    className="form-control"
                    placeholder="New password"
                    value={formData.new_password}
                    onChange={handleInputChange}
                    required
                    disabled={isPending}
                />
                <button
                    type="button"
                    className="position-absolute top-50 end-0 translate-middle-y border-0 bg-transparent"
                    style={{ right: '10px' }}
                    onClick={togglePasswordVisibility}
                >
                    {passwordVisible ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
                {errors.new_password && <p className="text-red-500 text-sm">{errors.new_password}</p>}
            </div>
            {/* Password Strength Indicators */}
            <ul className="text-sm mt-2 list-unstyled">
                <li className={passwordStrength.length ? 'text-green-500' : 'text-red-500'}>
                    {passwordStrength.length ? '✅' : '❌'} At least 8 characters
                </li>
                <li className={passwordStrength.uppercase ? 'text-green-500' : 'text-red-500'}>
                    {passwordStrength.uppercase ? '✅' : '❌'} Contains an uppercase letter
                </li>
                <li className={passwordStrength.lowercase ? 'text-green-500' : 'text-red-500'}>
                    {passwordStrength.lowercase ? '✅' : '❌'} Contains a lowercase letter
                </li>
                <li className={passwordStrength.number ? 'text-green-500' : 'text-red-500'}>
                    {passwordStrength.number ? '✅' : '❌'} Contains a number
                </li>
                <li className={passwordStrength.special ? 'text-green-500' : 'text-red-500'}>
                    {passwordStrength.special ? '✅' : '❌'} Contains a special character (!@#$%^&*)
                </li>
            </ul>

            <div className="position-relative">
                <label htmlFor="confirm_password" className="pt-2" style={{ fontSize: '0.875rem' }}>
                    Confirm New Password:
                </label>
                <input
                    type={passwordVisible ? 'text' : 'password'}
                    id="confirm_password"
                    name="confirm_password"
                    className="form-control"
                    placeholder="Confirm Password"
                    value={formData.confirm_password}
                    onChange={handleInputChange}
                    required
                    disabled={isPending}
                />
                <button
                    type="button"
                    className="position-absolute top-50 end-0 translate-middle-y border-0 bg-transparent"
                    style={{ right: '10px' }}
                    onClick={togglePasswordVisibility}
                >
                    {passwordVisible ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
                {errors.confirm_password && <p className="text-red-500 text-sm">{errors.confirm_password}</p>}
            </div>
            <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.9 }}
                type="submit"
                className="btn btn-danger mt-2"
                disabled={isPending}
            >
                {isPending ? 'Resetting...' : 'Change Password'}
            </motion.button>
            <AnimatePresence>
                {login && (
                    <motion.button
                        type="button"
                        className="btn btn-primary mt-2 ms-2"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.9 }}
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                    >
                        <Link className="text-white text-decoration-none" to="/">
                            Login
                        </Link>
                    </motion.button>
                )}
            </AnimatePresence>
        </motion.form>
    );
};

export default PasswordResetForm;
